/**
 * Register audit rows still owed after a landed write, persisted per user (13-23, D-13-L-5).
 *
 * After 1 Dec a missing sibling row is a hole in the system of record, so in-tab only (D-13-13-2, the ISS-206
 * class) is not enough for the registers. When a lodge or a verb lands but some of its sibling drafts do not post,
 * the Writer calls `owe(upn, ref, drafts)` before it returns. `retryOwed(io, actor)` posts them later, on load and
 * on the record page, through `postRecordAudit`'s de-dupe against a fresh `ParentRef` read (D-13-07-3).
 *
 *   - **Storage:** `localStorage['qms.recordAudit.v1']`, a JSON array of entries, one per (UPN, ref):
 *     `{ upn, ref, drafts, firstOwedAt }` (D-13-23-2), plus a short-lived hold while a tab retries it. It never
 *     shares a key with the outbox (`qms.outbox.v1`) and register verbs never go through the outbox (D-13-L-4).
 *   - **Per entry, read-merge-write:** every change re-reads the array and rewrites only its own entry, so two tabs
 *     do not clobber each other (D-13-06-3). A tab claims an entry before retrying it; another tab's claim is
 *     respected until it is 60 s stale, so two tabs never post the same rows at once.
 *   - **Never as the wrong user:** `retryOwed` touches only the signed-in actor's entries. Another UPN's entries
 *     stay stored, untouched (D-13-06-2). No actor, nothing runs.
 *   - **Storage unavailable** (private mode, blocked, full): every call is in try/catch; the store degrades to memory
 *     for the rest of the page load and `note()` says so, for the caller to show.
 */

import { browserStorage } from '../lib/outbox';
import type { OutboxStorage } from '../lib/outbox';
import { postRecordAudit } from './recordService';
import type { RecordIO } from './recordService';
import type { Actor } from '../types/actionRegister';
import type { SiblingDraft } from '../types/records';

/** The one storage key (never `qms.outbox.v1`). */
export const RECORD_AUDIT_STORAGE_KEY = 'qms.recordAudit.v1';

/** Another tab's hold on an entry is respected for this long (the outbox's stale window, D-13-06-3). */
export const OWED_HOLD_STALE_MS = 60_000;

/** What `note()` returns when this browser is not keeping owed rows. */
export const NOT_DURABLE_NOTE =
  'This browser is not saving the audit rows still to post, so they are kept in this tab only. Keep the tab open until they post.';

/** One record's owed rows for one person (D-13-23-2). */
export interface OwedEntry {
  /** Lower-cased UPN of the person whose write owes the rows. */
  upn: string;
  /** The composed ref, `<PREFIX>-<Id>`. */
  ref: string;
  /** The sibling drafts still to post, each with its `[a:<id>]` tag. */
  drafts: SiblingDraft[];
  /** When the first of them became owed (UTC ISO). */
  firstOwedAt: string;
  /** The tab retrying it, and since when. */
  heldBy?: string;
  heldAt?: string;
}

export interface RecordAuditStore {
  /** Add drafts to the person's entry for `ref` (merged by tag; the earliest `firstOwedAt` kept). */
  owe(upn: string, ref: string, drafts: readonly SiblingDraft[]): void;
  /** The person's entries, oldest first. */
  owedFor(upn: string): OwedEntry[];
  /**
   * After a retry: remove what posted. With `attempted`, only those drafts are replaced by `stillOwed`, so a draft
   * another tab owed meanwhile is kept; without it, the entry becomes exactly `stillOwed`. Empty → the entry goes.
   * The hold is released either way.
   */
  settle(upn: string, ref: string, stillOwed: readonly SiblingDraft[], attempted?: readonly SiblingDraft[]): void;
  /** Hold the entry for this tab, unless another tab holds it (and is not stale). Returns the entry, or null. */
  claim(upn: string, ref: string, holder: string): OwedEntry | null;
  /** Release a hold without settling (the retry could not run). */
  release(upn: string, ref: string, holder: string): void;
  /** False once storage has failed: entries then live in memory for this page load only. */
  readonly durable: boolean;
  /** A sentence for the caller to show when not durable; otherwise null. */
  note(): string | null;
}

export interface RecordAuditStoreOptions {
  /** Default `localStorage`. */
  storage?: OutboxStorage;
  /** Epoch ms. Default `Date.now`. */
  now?: () => number;
}

const lower = (s: string | null | undefined) => (s ?? '').trim().toLowerCase();

/** A draft's identity in an entry: its tag, row kind and changed column or log kind. */
function draftKey(d: SiblingDraft): string {
  return [d.auditId ?? '', d.EntryType ?? '', d.FieldChanged ?? '', d.LogKind ?? ''].join('|');
}

function isDraft(x: unknown): x is SiblingDraft {
  if (typeof x !== 'object' || x === null) return false;
  const d = x as Partial<SiblingDraft>;
  return typeof d.Title === 'string' && typeof d.EntryType === 'string' && typeof d.Note === 'string' && typeof d.auditId === 'string';
}

function isEntry(x: unknown): x is OwedEntry {
  if (typeof x !== 'object' || x === null) return false;
  const e = x as Partial<OwedEntry>;
  return (
    typeof e.upn === 'string' &&
    e.upn !== '' &&
    typeof e.ref === 'string' &&
    e.ref !== '' &&
    typeof e.firstOwedAt === 'string' &&
    Array.isArray(e.drafts) &&
    e.drafts.every(isDraft)
  );
}

function mergeDrafts(a: readonly SiblingDraft[], b: readonly SiblingDraft[]): SiblingDraft[] {
  const out = [...a];
  const seen = new Set(a.map(draftKey));
  for (const d of b) {
    if (!seen.has(draftKey(d))) {
      seen.add(draftKey(d));
      out.push(d);
    }
  }
  return out;
}

/** A store over `storage` (default `localStorage`). */
export function createRecordAuditStore(opts: RecordAuditStoreOptions = {}): RecordAuditStore {
  let storage: OutboxStorage | null = null;
  try {
    storage = opts.storage ?? browserStorage();
  } catch {
    storage = null;
  }
  const now = opts.now ?? Date.now;
  let durable = storage !== null;
  /** The truth while not durable (and a mirror of the last good read while durable). */
  let memory: OwedEntry[] = [];

  const read = (): OwedEntry[] => {
    if (!durable || !storage) return memory.map((e) => ({ ...e, drafts: [...e.drafts] }));
    try {
      const raw = storage.get(RECORD_AUDIT_STORAGE_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      memory = Array.isArray(parsed) ? parsed.filter(isEntry) : [];
    } catch (err) {
      // A JSON error is a corrupt value, not unavailable storage: keep what memory holds and stay durable.
      if (!(err instanceof SyntaxError)) durable = false;
    }
    return memory.map((e) => ({ ...e, drafts: [...e.drafts] }));
  };

  const write = (rows: OwedEntry[]): void => {
    memory = rows;
    if (!durable || !storage) return;
    try {
      storage.set(RECORD_AUDIT_STORAGE_KEY, JSON.stringify(rows));
    } catch {
      durable = false;
    }
  };

  /** Read-merge-write one entry: `fn` gets the stored entry (or undefined) and returns its replacement or null. */
  const update = (upn: string, ref: string, fn: (e: OwedEntry | undefined) => OwedEntry | null): OwedEntry | null => {
    const u = lower(upn);
    const rows = read();
    const i = rows.findIndex((e) => lower(e.upn) === u && e.ref === ref);
    const next = fn(i >= 0 ? rows[i] : undefined);
    if (i >= 0) rows.splice(i, 1);
    if (next) rows.push(next);
    write(rows);
    return next;
  };

  // Probe once, so `durable` is known before the first owe.
  if (storage) {
    try {
      const raw = storage.get(RECORD_AUDIT_STORAGE_KEY);
      storage.set(RECORD_AUDIT_STORAGE_KEY, raw ?? '[]');
    } catch {
      durable = false;
    }
  }

  const held = (e: OwedEntry, holder: string): boolean => {
    if (!e.heldBy || e.heldBy === holder) return false;
    const at = Date.parse(e.heldAt ?? '');
    return !Number.isNaN(at) && now() - at < OWED_HOLD_STALE_MS;
  };

  return {
    owe(upn, ref, drafts) {
      const u = lower(upn);
      if (!u || !ref || drafts.length === 0) return;
      const stamp = new Date(now()).toISOString();
      update(u, ref, (e) => ({
        upn: u,
        ref,
        drafts: mergeDrafts(e?.drafts ?? [], drafts),
        firstOwedAt: e?.firstOwedAt && e.firstOwedAt < stamp ? e.firstOwedAt : stamp,
        ...(e?.heldBy ? { heldBy: e.heldBy, heldAt: e.heldAt } : {}),
      }));
    },
    owedFor(upn) {
      const u = lower(upn);
      if (!u) return [];
      return read()
        .filter((e) => lower(e.upn) === u)
        .sort((a, b) => a.firstOwedAt.localeCompare(b.firstOwedAt));
    },
    settle(upn, ref, stillOwed, attempted) {
      update(upn, ref, (e) => {
        if (!e) return stillOwed.length ? { upn: lower(upn), ref, drafts: [...stillOwed], firstOwedAt: new Date(now()).toISOString() } : null;
        const tried = new Set((attempted ?? e.drafts).map(draftKey));
        const kept = e.drafts.filter((d) => !tried.has(draftKey(d)));
        const drafts = mergeDrafts(kept, stillOwed);
        return drafts.length ? { upn: e.upn, ref, drafts, firstOwedAt: e.firstOwedAt } : null;
      });
    },
    claim(upn, ref, holder) {
      let claimed: OwedEntry | null = null;
      update(upn, ref, (e) => {
        if (!e) return null;
        if (held(e, holder)) return e;
        claimed = { ...e, heldBy: holder, heldAt: new Date(now()).toISOString() };
        return claimed;
      });
      return claimed;
    },
    release(upn, ref, holder) {
      update(upn, ref, (e) => {
        if (!e) return null;
        if (e.heldBy !== holder) return e;
        return { upn: e.upn, ref: e.ref, drafts: e.drafts, firstOwedAt: e.firstOwedAt };
      });
    },
    get durable() {
      return durable;
    },
    note() {
      return durable ? null : NOT_DURABLE_NOTE;
    },
  };
}

// ---------------------------------------------------------------------------
// The default store (this browser's localStorage), and the plan's module functions over it
// ---------------------------------------------------------------------------

let defaultStore: RecordAuditStore | null = null;

/** The page's one store over `localStorage` (created on first use). */
export function defaultRecordAuditStore(): RecordAuditStore {
  if (!defaultStore) defaultStore = createRecordAuditStore();
  return defaultStore;
}

/** Test hook: forget the default store. */
export function resetDefaultRecordAuditStore(): void {
  defaultStore = null;
}

export function owe(upn: string, ref: string, drafts: readonly SiblingDraft[]): void {
  defaultRecordAuditStore().owe(upn, ref, drafts);
}

export function owedFor(upn: string): OwedEntry[] {
  return defaultRecordAuditStore().owedFor(upn);
}

export function settle(upn: string, ref: string, stillOwed: readonly SiblingDraft[], attempted?: readonly SiblingDraft[]): void {
  defaultRecordAuditStore().settle(upn, ref, stillOwed, attempted);
}

// ---------------------------------------------------------------------------
// Retry
// ---------------------------------------------------------------------------

export interface RetryOwedResult {
  /** Drafts that posted (or were found already posted) in this run. */
  posted: number;
  /** The actor's entries still owed after the run (incl. any another tab is holding). */
  stillOwed: OwedEntry[];
  /** Entries another tab was retrying, left alone. */
  skippedHeld: number;
  durable: boolean;
  note: string | null;
}

export interface RetryOwedOptions {
  store?: RecordAuditStore;
  /** Default `recordService.postRecordAudit` (the de-duped poster). */
  post?: (io: RecordIO, ref: string, drafts: readonly SiblingDraft[]) => Promise<SiblingDraft[]>;
  /** This tab's id for the hold. Default a random uuid per call. */
  holder?: string;
  /** Only this record's entry (the record page). Default every entry of the actor (on load). */
  ref?: string;
}

/**
 * Post the actor's owed rows, entry by entry, each through `postRecordAudit` (one fresh read per record, de-duped;
 * a failed read posts nothing). Entries of any other UPN are never read for posting, claimed or changed.
 */
export async function retryOwed(io: RecordIO, actor: Pick<Actor, 'upn'>, opts: RetryOwedOptions = {}): Promise<RetryOwedResult> {
  const store = opts.store ?? defaultRecordAuditStore();
  const post = opts.post ?? postRecordAudit;
  const holder = opts.holder ?? (globalThis.crypto?.randomUUID?.() ?? `tab-${Date.now()}`);
  const upn = lower(actor?.upn);
  let posted = 0;
  let skippedHeld = 0;
  if (upn) {
    for (const entry of store.owedFor(upn)) {
      if (opts.ref !== undefined && entry.ref !== opts.ref) continue;
      const mine = store.claim(upn, entry.ref, holder);
      if (!mine) {
        skippedHeld += 1;
        continue;
      }
      let still: SiblingDraft[];
      try {
        still = await post(io, mine.ref, mine.drafts);
      } catch {
        store.release(upn, mine.ref, holder);
        continue;
      }
      posted += mine.drafts.length - still.length;
      store.settle(upn, mine.ref, still, mine.drafts);
    }
  }
  return {
    posted,
    stillOwed: upn ? store.owedFor(upn) : [],
    skippedHeld,
    durable: store.durable,
    note: store.note(),
  };
}
